/**
 * Constructor Fantasy Points Calculation
 *
 * This module contains the logic for calculating constructor fantasy points.
 * Constructors score combined points from both drivers plus constructor-specific bonuses.
 *
 * Constructor-specific scoring:
 * - Teamwork bonus: Based on Q2/Q3 qualification
 * - Pit stop time points: Based on fastest pit stop time (0-20 points)
 * - Fastest pit stop bonus: +5 for overall fastest pit stop
 * - World record pit stop bonus: +15 for world record pit stop
 * - DQ penalties: -10 per DQ'd driver (qualifying), -30 per DQ'd driver (race/sprint)
 */

import { DEFAULT_SEASON } from "./constants";
import {
  getQualifyingPoints,
  getRacePoints,
  getSprintPoints,
  NO_QUALIFYING_TIME_PENALTY,
} from "./fantasy-points";
import { getScoringRules } from "./scoring-rules";

// Constructor qualifying bonus points
export const Q_BONUS = {
  NEITHER_Q2: -1, // Neither driver reached Q2
  ONE_Q2: 1, // One driver reached Q2
  BOTH_Q2: 3, // Both drivers reached Q2
  ONE_Q3: 5, // One driver reached Q3
  BOTH_Q3: 10, // Both drivers reached Q3
} as const;

// Pit stop points based on time
export const PIT_STOP_POINTS = {
  OVER_3S: 0, // 3.0s or more
  RANGE_2_5_3S: 2, // 2.5s - 2.99s
  RANGE_2_2_2_5S: 5, // 2.2s - 2.49s
  RANGE_2_0_2_2S: 10, // 2.0s - 2.19s
  UNDER_2S: 20, // Under 2.0s
  FASTEST_BONUS: 5, // Fastest pit stop of the race
  WORLD_RECORD_BONUS: 15, // World record pit stop
} as const;

// DQ penalties
export const DQ_PENALTIES = {
  QUALIFYING: -10, // Per DQ'd driver in qualifying (includes -5 NC penalty)
  RACE: -30, // Per DQ'd driver in race (includes -20 DNF penalty)
  SPRINT: -20, // Per DQ'd driver in sprint (includes -10 DNF penalty) — unconfirmed, no sprint DQ seen yet
} as const;

/**
 * Calculate teamwork bonus based on qualifying positions
 *
 * Rules:
 * - Both drivers in Q3 (P1-P10): +10
 * - One driver in Q3: +5
 * - Both drivers in Q2 (P11-P15): +3
 * - One driver in Q2: +1
 * - Neither driver in Q2: -1
 *
 * @param pos1 - First driver's qualifying position (null = no time set)
 * @param pos2 - Second driver's qualifying position (null = no time set)
 * @returns Teamwork bonus points
 */
export function calculateTeamworkBonus(
  pos1: number | null,
  pos2: number | null,
): number {
  // Helper to check if a position reached Q3 (top 10)
  const inQ3 = (pos: number | null) => pos !== null && pos <= 10;
  // Helper to check if a position reached Q2 (top 15)
  const inQ2 = (pos: number | null) => pos !== null && pos <= 15;

  if (inQ3(pos1) && inQ3(pos2)) {
    return Q_BONUS.BOTH_Q3; // +10
  }
  if (inQ3(pos1) || inQ3(pos2)) {
    return Q_BONUS.ONE_Q3; // +5
  }
  if (inQ2(pos1) && inQ2(pos2)) {
    return Q_BONUS.BOTH_Q2; // +3
  }
  if (inQ2(pos1) || inQ2(pos2)) {
    return Q_BONUS.ONE_Q2; // +1
  }
  // Neither driver reached Q2 (both in Q1 or didn't set a time)
  return Q_BONUS.NEITHER_Q2; // -1
}

/**
 * Calculate pit stop time-based points
 *
 * Rules:
 * - Under 2.0s: 20 points
 * - 2.0s - 2.19s: 10 points
 * - 2.2s - 2.49s: 5 points
 * - 2.5s - 2.99s: 2 points
 * - 3.0s or more: 0 points
 *
 * @param pitStopTime - Fastest pit stop time in seconds
 * @returns Pit stop time points (0-20)
 */
export function calculatePitStopTimePoints(pitStopTime: number | null): number {
  if (pitStopTime === null || pitStopTime <= 0) {
    return 0;
  }

  if (pitStopTime < 2.0) {
    return PIT_STOP_POINTS.UNDER_2S; // 20
  }
  if (pitStopTime < 2.2) {
    return PIT_STOP_POINTS.RANGE_2_0_2_2S; // 10
  }
  if (pitStopTime < 2.5) {
    return PIT_STOP_POINTS.RANGE_2_2_2_5S; // 5
  }
  if (pitStopTime < 3.0) {
    return PIT_STOP_POINTS.RANGE_2_5_3S; // 2
  }
  return PIT_STOP_POINTS.OVER_3S; // 0
}

/**
 * Input data for constructor fantasy calculation
 */
export interface ConstructorFantasyInput {
  // Qualifying data
  qualifyingPosition1: number | null; // First driver's qualifying position
  qualifyingPosition2: number | null; // Second driver's qualifying position
  qualifyingDqPoints?: number; // DQ penalty from database (-10 per DQ'd driver)

  // Sprint data (only for sprint weekends)
  sprintPosition1?: number | null; // First driver's sprint position
  sprintPosition2?: number | null; // Second driver's sprint position
  sprintDnf1?: boolean; // First driver DNF in sprint
  sprintDnf2?: boolean; // Second driver DNF in sprint
  sprintPositionsGained?: number; // Combined positions gained in sprint
  sprintOvertakes?: number; // Combined overtakes in sprint
  sprintFastestLap?: boolean; // Constructor had fastest lap in sprint
  sprintDqPoints?: number; // DQ penalty from database (-30 per DQ'd driver)
  sprintDnfCount?: number; // Number of DNFs in sprint (includes DQ'd drivers)

  // Race data
  racePosition1: number | null; // First driver's race position
  racePosition2: number | null; // Second driver's race position
  raceDnf1: boolean; // First driver DNF in race
  raceDnf2: boolean; // Second driver DNF in race
  positionsGained: number; // Combined positions gained in race
  overtakes: number; // Combined overtakes in race
  fastestLap: boolean; // Constructor had fastest lap in race
  pitStopTimePoints?: number; // Pit stop time-based points from database (already includes fastest bonus)
  overallFastestPitStop?: boolean; // Metadata: had overall fastest pit stop (bonus already in pitStopTimePoints)
  worldRecordPitStop?: boolean; // Had world record pit stop
  raceDqPoints?: number; // DQ penalty from database (-30 per DQ'd driver)
  teamworkPoints?: number | null; // Teamwork bonus from API (uses effective positions after DQs)
  dnfCount: number; // Number of DNFs in race (includes DQ'd drivers)
}

/**
 * Output breakdown of constructor fantasy points
 */
export interface ConstructorFantasyBreakdown {
  // Qualifying
  qualifyingPts: number; // Combined qualifying position points
  teamworkPts: number; // Teamwork bonus
  qualifyingDqPts: number; // Qualifying DQ penalty

  // Sprint (only for sprint weekends)
  sprintPosPts: number; // Combined sprint position points
  sprintPgPts: number; // Sprint positions gained
  sprintOvPts: number; // Sprint overtakes
  sprintFlPts: number; // Sprint fastest lap
  sprintDqPts: number; // Sprint DQ penalty
  sprintDnfPts: number; // Sprint DNF penalty

  // Race
  racePosPts: number; // Combined race position points
  racePgPts: number; // Race positions gained
  raceOvPts: number; // Race overtakes
  raceFlPts: number; // Race fastest lap
  pitTimePts: number; // Pit stop time-based points
  raceFpPts: number; // Fastest pit stop bonus
  dnfPts: number; // Race DNF penalty
  raceDqPts: number; // Race DQ penalty

  // Total
  total: number; // Sum of all points
}

/**
 * Calculate constructor fantasy points breakdown
 *
 * This function calculates all fantasy points for a constructor based on
 * the combined performance of both drivers plus constructor-specific bonuses.
 *
 * Key rules:
 * - Qualifying: Combined position points + teamwork bonus
 * - Race: Combined position points + PG + OV + FL + pit stop points
 * - DQ handling: DQ penalty includes DNF penalty, so exclude DQ'd drivers from DNF count
 *
 * @param input - Constructor fantasy input data
 * @param isSprintWeekend - Whether this is a sprint weekend
 * @returns Breakdown of all fantasy points
 */
export function calculateConstructorFantasyBreakdown(
  input: ConstructorFantasyInput,
  isSprintWeekend: boolean,
  season = DEFAULT_SEASON,
): ConstructorFantasyBreakdown {
  const rules = getScoringRules(season);

  // === QUALIFYING ===

  // DQ penalty for qualifying (from database - constructor-specific penalty)
  // This is -10 per DQ'd driver (includes -5 NC penalty + -5 constructor penalty)
  const qualifyingDqPts = input.qualifyingDqPoints || 0;

  // Calculate number of DQ'd drivers in qualifying from the DQ penalty
  // qualifyingDqPoints = dqQualifying per DQ'd driver
  const numQualifyingDqDrivers =
    qualifyingDqPts !== 0
      ? Math.abs(qualifyingDqPts) / Math.abs(rules.dqQualifying)
      : 0;

  // Qualifying points (combined from both drivers)
  // IMPORTANT: For DQ'd drivers, their -5 NC penalty is already included in qualifyingDqPts
  // So we need to exclude the -5 NC penalty from qualifyingPts for DQ'd drivers
  const q1Pts = getQualifyingPoints(input.qualifyingPosition1);
  const q2Pts = getQualifyingPoints(input.qualifyingPosition2);
  // Subtract the double-counted NC penalties (-5 per DQ'd driver)
  const qualifyingPts =
    q1Pts + q2Pts - numQualifyingDqDrivers * NO_QUALIFYING_TIME_PENALTY;

  // Teamwork bonus — use stored API value when available (handles effective positions after DQs),
  // fall back to computing from qualifying positions for prediction/historical modes.
  const teamworkPts =
    input.teamworkPoints !== undefined && input.teamworkPoints !== null
      ? input.teamworkPoints
      : calculateTeamworkBonus(
          input.qualifyingPosition1,
          input.qualifyingPosition2,
        );

  // === SPRINT (only for sprint weekends) ===

  let sprintPosPts = 0;
  let sprintPgPts = 0;
  let sprintOvPts = 0;
  let sprintFlPts = 0;
  let sprintDqPts = 0;
  let sprintDnfPts = 0;

  if (isSprintWeekend) {
    // Sprint position points (combined from both drivers)
    const sprint1Pts = input.sprintDnf1
      ? 0
      : getSprintPoints(input.sprintPosition1 ?? null);
    const sprint2Pts = input.sprintDnf2
      ? 0
      : getSprintPoints(input.sprintPosition2 ?? null);
    sprintPosPts = sprint1Pts + sprint2Pts;
    sprintPgPts = input.sprintPositionsGained || 0;
    sprintOvPts = input.sprintOvertakes || 0;
    sprintFlPts = input.sprintFastestLap ? rules.fastestLapSprint : 0;
    sprintDqPts = input.sprintDqPoints || 0;

    // Calculate number of DQ'd drivers in sprint from the DQ penalty
    // sprintDqPoints = dqSprint per DQ'd driver (includes DNF penalty)
    const numSprintDqDrivers =
      sprintDqPts !== 0 ? Math.abs(sprintDqPts) / Math.abs(rules.dqSprint) : 0;

    // Sprint DNF penalty - exclude DQ'd drivers since their DNF is already in sprintDqPts
    const actualSprintDnfCount = Math.max(
      0,
      (input.sprintDnfCount || 0) - numSprintDqDrivers,
    );
    // Use || 0 to convert -0 to 0 (JavaScript quirk with Math.max)
    sprintDnfPts = actualSprintDnfCount * rules.sprintDnfPenalty || 0;
  }

  // === RACE ===

  // Race position points (combined from both drivers)
  const race1Pts = input.raceDnf1 ? 0 : getRacePoints(input.racePosition1);
  const race2Pts = input.raceDnf2 ? 0 : getRacePoints(input.racePosition2);
  const racePosPts = race1Pts + race2Pts;
  const racePgPts = input.positionsGained || 0;
  const raceOvPts = input.overtakes || 0;
  const raceFlPts = input.fastestLap ? rules.fastestLapRace : 0;

  // Pit stop time-based points (from database - 0-20 based on pit stop time)
  const pitTimePts = input.pitStopTimePoints || 0;

  // Pit stop bonus points — the "Fastest Pitstop" API event Value already includes the fastest
  // bonus. raceFpPts is only used for worldRecordPitStop (a separate event if it ever occurs).
  // overallFastestPitStop is metadata only and does NOT add extra points here.
  let raceFpPts = 0;
  if (input.worldRecordPitStop) {
    raceFpPts += PIT_STOP_POINTS.WORLD_RECORD_BONUS;
  }

  // DQ penalty for race (from database - constructor-specific penalty)
  // This is dqRace per DQ'd driver (includes DNF + constructor penalty)
  const raceDqPts = input.raceDqPoints || 0;

  // Calculate number of DQ'd drivers from the DQ penalty
  const numRaceDqDrivers =
    raceDqPts !== 0 ? Math.abs(raceDqPts) / Math.abs(rules.dqRace) : 0;

  // DNF penalty (race only - sprint DNF is separate)
  // IMPORTANT: Exclude DQ'd drivers from DNF count since their DNF is already in raceDqPts
  // The API's dnfCount includes DQ'd drivers, so we subtract them
  const actualDnfCount = Math.max(0, input.dnfCount - numRaceDqDrivers);
  // Use || 0 to convert -0 to 0 (JavaScript quirk with Math.max)
  const dnfPts = actualDnfCount * rules.raceDnfPenalty || 0;

  // === TOTAL ===

  const total =
    qualifyingPts +
    teamworkPts +
    qualifyingDqPts +
    sprintPosPts +
    sprintPgPts +
    sprintOvPts +
    sprintFlPts +
    sprintDqPts +
    sprintDnfPts +
    racePosPts +
    racePgPts +
    raceOvPts +
    raceFlPts +
    pitTimePts +
    raceFpPts +
    dnfPts +
    raceDqPts;

  return {
    qualifyingPts,
    teamworkPts,
    qualifyingDqPts,
    sprintPosPts,
    sprintPgPts,
    sprintOvPts,
    sprintFlPts,
    sprintDqPts,
    sprintDnfPts,
    racePosPts,
    racePgPts,
    raceOvPts,
    raceFlPts,
    pitTimePts,
    raceFpPts,
    dnfPts,
    raceDqPts,
    total,
  };
}
